import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import type {
  AppController,
  AppSnapshot,
  ExecuteAction,
  GoalKind,
  GoalSettings,
  NutrientGoal,
  NutrientUnit,
} from "../types";
import {
  normalizeNutrientUnit,
} from "../../domain/nutrition/nutrients";
import {
  getNutrientDefinition,
  NUTRIENT_CATALOG,
} from "../../domain/nutrition/nutrient-catalog";

interface MoreViewProps {
  snapshot: AppSnapshot;
  controller: AppController;
  execute: ExecuteAction;
  onCheckIn: () => void;
}

type SettingsPanelProps = Omit<MoreViewProps, "onCheckIn">;

interface GoalNutrientDefinition {
  readonly id: string;
  readonly name: string;
  readonly category: "other" | "vitamin" | "mineral";
  readonly unit: NutrientUnit;
}

const FIBRE_GOAL_DEFINITION: GoalNutrientDefinition = Object.freeze({
  id: "fibre",
  name: "Fibre",
  category: "other",
  unit: "g",
});

const GOAL_NUTRIENTS: readonly GoalNutrientDefinition[] = Object.freeze([
  FIBRE_GOAL_DEFINITION,
  ...NUTRIENT_CATALOG,
]);

export function MoreView({ snapshot, controller, execute, onCheckIn }: MoreViewProps) {
  return (
    <section className="page-section" aria-labelledby="more-heading">
      <header className="page-heading"><div><p className="eyebrow">Preferences and data</p><h1 id="more-heading">More</h1></div></header>

      <button className="menu-card" type="button" onClick={onCheckIn}>
        <span><strong>Weekly check-in</strong><small>{checkInLabel(snapshot)}</small></span>
        <span aria-hidden="true">›</span>
      </button>

      <GoalSettingsPanel snapshot={snapshot} controller={controller} execute={execute} />
      <NutrientGoalsPanel snapshot={snapshot} controller={controller} execute={execute} />
      <BackupPanel snapshot={snapshot} controller={controller} execute={execute} />

      <article className="card privacy-card">
        <div><strong>Local-first by design</strong><p>Your diary and weight data stay in this browser unless you choose to back them up to Google Drive.</p></div>
      </article>
    </section>
  );
}

function GoalSettingsPanel({ snapshot, controller, execute }: SettingsPanelProps) {
  const [settings, setSettings] = useState(snapshot.goals);
  const [saving, setSaving] = useState(false);
  useEffect(() => setSettings(snapshot.goals), [snapshot.goals]);

  const proteinCalories = snapshot.week.fixedDailyProteinGrams * 4;
  const baseline = Math.max(snapshot.week.baselineDailyCalories, proteinCalories);
  const proteinPercent = (proteinCalories / baseline) * 100;
  const remainderPercent = Math.max(0, 100 - proteinPercent);
  const carbPercent = remainderPercent * settings.carbohydrateShareOfRemainder;
  const fatPercent = remainderPercent * settings.fatShareOfRemainder;
  const chartStyle = {
    "--protein-stop": `${proteinPercent}%`,
    "--carb-stop": `${proteinPercent + carbPercent}%`,
  } as CSSProperties;

  function chooseGoal(kind: GoalKind) {
    setSettings((current) => ({ ...current, kind }));
  }

  function setCarbohydratePercent(value: number) {
    const carbohydrateShareOfRemainder = Math.max(0, Math.min(0.95, value / 100));
    setSettings((current) => ({
      ...current,
      carbohydrateShareOfRemainder,
      fatShareOfRemainder: 1 - carbohydrateShareOfRemainder,
    }));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    await execute(() => controller.saveGoalSettings(settings), { successMessage: "Goals and macro preferences saved." });
    setSaving(false);
  }

  return (
    <article className="card settings-card">
      <div className="card__heading"><div><h2>Goal and macros</h2><p>Used to prepare recommendations at each check-in.</p></div></div>
      <form className="stack" onSubmit={save}>
        <fieldset className="fieldset">
          <legend>Long-term goal</legend>
          <div className="segmented-control segmented-control--three">
            {(["lose", "maintain", "gain"] as const).map((kind) => (
              <label key={kind} className={settings.kind === kind ? "segmented-control__item segmented-control__item--selected" : "segmented-control__item"}>
                <input type="radio" name="goal" checked={settings.kind === kind} onChange={() => chooseGoal(kind)} />
                <span>{kind === "lose" ? "Lose" : kind === "gain" ? "Gain" : "Maintain"}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {settings.kind === "maintain" ? (
          <div className="form-grid">
            <label className="field"><span>Goal weight</span><span className="input-with-suffix"><input type="number" min="20" max="400" step="0.1" value={settings.maintenanceGoalWeightKg} onChange={(event) => setSettings({ ...settings, maintenanceGoalWeightKg: Number(event.target.value) })} /><i>kg</i></span></label>
            <label className="field"><span>Tolerance</span><span className="input-with-suffix"><input type="number" min="0" max="20" step="0.1" value={settings.maintenanceTolerancePercent} onChange={(event) => setSettings({ ...settings, maintenanceTolerancePercent: Number(event.target.value) })} /><i>± %</i></span></label>
          </div>
        ) : (
          <label className="field"><span>Target rate per week</span><span className="input-with-suffix"><input type="number" min="0.05" max="5" step="0.05" value={settings.weeklyRatePercent} onChange={(event) => setSettings({ ...settings, weeklyRatePercent: Number(event.target.value) })} /><i>% body weight</i></span><small>Recommendations are still limited by healthy adjustment safeguards.</small></label>
        )}

        <div className="field">
          <span>Weekly check-in day</span>
          <div className="read-only-field">
            <strong>{snapshot.checkIn.nominatedDayLabel}</strong>
            <small>Fixed for the current plan so week boundaries and existing entries stay consistent.</small>
          </div>
        </div>

        <fieldset className="fieldset macro-preference">
          <legend>Daily macro distribution</legend>
          <div className="macro-preference__visual">
            <div
              className="macro-pie"
              style={chartStyle}
              role="img"
              aria-label={`At the current baseline: ${Math.round(proteinPercent)} percent protein, ${Math.round(carbPercent)} percent carbohydrate and ${Math.round(fatPercent)} percent fat`}
            ><span>{snapshot.week.fixedDailyProteinGrams.toFixed(0)}<small>g protein</small></span></div>
            <ul className="macro-key">
              <li><i className="macro-key__protein" /><span>Protein</span><strong>{Math.round(proteinPercent)}%</strong></li>
              <li><i className="macro-key__carbs" /><span>Carbs</span><strong>{Math.round(carbPercent)}%</strong></li>
              <li><i className="macro-key__fat" /><span>Fat</span><strong>{Math.round(fatPercent)}%</strong></li>
            </ul>
          </div>
          <label className="range-field">
            <span><strong>Split remaining calories</strong><output>{Math.round(settings.carbohydrateShareOfRemainder * 100)}% carbs · {Math.round(settings.fatShareOfRemainder * 100)}% fat</output></span>
            <input type="range" min="0" max="95" step="1" value={Math.round(settings.carbohydrateShareOfRemainder * 100)} onChange={(event) => setCarbohydratePercent(Number(event.target.value))} />
            <small><span>More fat</span><span>More carbs</span></small>
          </label>
          <p className="field-hint">Protein is fixed for the week from your five-day trend weight: {proteinFactor(settings.kind)}. Only the calories left after protein are split here.</p>
        </fieldset>

        <button className="button button--primary button--full" type="submit" disabled={saving}>{saving ? "Saving…" : "Save goals"}</button>
      </form>
    </article>
  );
}

function NutrientGoalsPanel({ snapshot, controller, execute }: SettingsPanelProps) {
  const [goals, setGoals] = useState(() => normalizeGoals(snapshot.nutrientGoals));
  const [newNutrientId, setNewNutrientId] = useState(GOAL_NUTRIENTS[0]?.id ?? "");
  const [newAmount, setNewAmount] = useState("");
  const [goalError, setGoalError] = useState<string>();
  const [saving, setSaving] = useState(false);
  useEffect(() => setGoals(normalizeGoals(snapshot.nutrientGoals)), [snapshot.nutrientGoals]);
  const selectedGoalIds = new Set(goals.map((goal) => goal.nutrientId));
  const availableGoalNutrients = GOAL_NUTRIENTS.filter((definition) => !selectedGoalIds.has(definition.id));
  const selectedNewNutrient = availableGoalNutrients.find((definition) => definition.id === newNutrientId);
  const parsedNewAmount = Number(newAmount);
  const canAddGoal = selectedNewNutrient !== undefined && Number.isFinite(parsedNewAmount) && parsedNewAmount > 0;

  useEffect(() => {
    if (!availableGoalNutrients.some((definition) => definition.id === newNutrientId)) {
      setNewNutrientId(availableGoalNutrients[0]?.id ?? "");
    }
  }, [goals, newNutrientId]);

  function updateGoal(index: number, changes: Partial<NutrientGoal>) {
    setGoals((current) => current.map((goal, goalIndex) => goalIndex === index ? { ...goal, ...changes } : goal));
    setGoalError(undefined);
  }

  function addGoal() {
    const amount = parsedNewAmount;
    const definition = goalNutrientDefinition(newNutrientId);
    if (!definition || !Number.isFinite(amount) || amount <= 0) return;
    if (goals.some((goal) => goal.nutrientId === definition.id)) {
      setGoalError(`${definition.name} already has a daily goal.`);
      return;
    }
    setGoals((current) => [...current, {
      nutrientId: definition.id,
      name: definition.name,
      amount,
      unit: definition.unit,
    }]);
    const nextDefinition = availableGoalNutrients.find((candidate) => candidate.id !== definition.id);
    setNewNutrientId(nextDefinition?.id ?? "");
    setNewAmount("");
    setGoalError(undefined);
  }

  async function save() {
    setSaving(true);
    await execute(() => controller.saveNutrientGoals(normalizeGoals(goals)), { successMessage: "Nutrient goals saved." });
    setSaving(false);
  }

  return (
    <article className="card settings-card">
      <div className="card__heading"><div><h2>Nutrient goals</h2><p>Track fibre, vitamins and minerals that matter to you.</p></div></div>
      {goals.length === 0 ? (
        <div className="empty-state empty-state--compact"><strong>No extra goals yet</strong><p>Add a nutrient below. Advanced details stay tucked away in your daily view.</p></div>
      ) : (
        <ul className="editable-goals">
          {goals.map((goal, index) => {
            const definition = goalNutrientDefinition(goal.nutrientId);
            const name = definition?.name ?? goal.name;
            const unit = definition?.unit ?? normalizeNutrientUnit(goal.unit);
            return (
              <li key={`${goal.nutrientId}-${index}`}>
                <div className="nutrient-goal-fields">
                  <strong>{name}</strong>
                  <label className="field">
                    <span>Daily amount</span>
                    <input
                      aria-label={`${name} daily amount`}
                      type="number"
                      min="0"
                      step="any"
                      value={goal.amount}
                      onChange={(event) => updateGoal(index, { amount: Math.max(0, Number(event.target.value) || 0) })}
                    />
                  </label>
                  <label className="field">
                    <span>Unit</span>
                    <output className="read-only-field" aria-label={`${name} goal unit`}><strong>{unit}</strong></output>
                  </label>
                </div>
                <button className="icon-button icon-button--danger" type="button" aria-label={`Remove ${name} goal`} onClick={() => setGoals((current) => current.filter((_, goalIndex) => goalIndex !== index))}>x</button>
              </li>
            );
          })}
        </ul>
      )}
      {availableGoalNutrients.length === 0 ? (
        <p className="field-hint">All available nutrients have daily goals.</p>
      ) : (
        <details className="details-panel">
          <summary>Add another nutrient</summary>
          <div className="add-goal-form details-panel__content">
            <label className="field">
              <span>Nutrient</span>
              <select
                aria-label="New nutrient name"
                value={newNutrientId}
                onChange={(event) => {
                  setNewNutrientId(event.target.value);
                  setNewAmount("");
                  setGoalError(undefined);
                }}
              >
                <GoalNutrientOptionGroups definitions={availableGoalNutrients} />
              </select>
            </label>
            <label className="field"><span>Daily target</span><input aria-label="New nutrient daily target" type="number" min="0" step="0.01" value={newAmount} onChange={(event) => setNewAmount(event.target.value)} /></label>
            <label className="field"><span>Unit</span><output className="read-only-field" aria-label="New nutrient goal unit"><strong>{selectedNewNutrient?.unit ?? "—"}</strong></output></label>
            <button className="button button--secondary" type="button" onClick={addGoal} disabled={!canAddGoal}>Add goal</button>
          </div>
        </details>
      )}
      {goalError && <p className="form-error" role="alert">{goalError}</p>}
      <button className="button button--primary button--full" type="button" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save nutrient goals"}</button>
    </article>
  );
}

function BackupPanel({ snapshot, controller, execute }: SettingsPanelProps) {
  const [restoring, setRestoring] = useState(false);
  const backup = snapshot.backup;

  async function restore() {
    if (!window.confirm("Restore the latest Google Drive backup? A safety backup of your current data will be created first, then local data will be replaced.")) return;
    setRestoring(true);
    const result = await execute(() => controller.restoreLatestBackup(), { errorMessage: "The backup could not be restored. Your existing local data has not been changed." });
    if (result.ok) {
      window.alert(`Restored ${result.value.recordCount.toLocaleString()} records from ${result.value.restoredFrom}.`);
    }
    setRestoring(false);
  }

  return (
    <article className="card settings-card">
      <div className="card__heading">
        <div><h2>Backup and restore</h2><p>Keep a private backup in your linked Google Drive account.</p></div>
        {backup.backupPending && <span className="status-pill status-pill--warning">Pending</span>}
      </div>
      {!snapshot.isOnline && <div className="notice notice--neutral"><div><strong>You’re offline</strong><p>Local tracking still works. Drive actions will be available when you reconnect.</p></div></div>}
      {backup.driveConnected ? (
        <>
          <dl className="definition-list">
            <div><dt>Google Drive</dt><dd>{backup.accountLabel ?? "Connected"}</dd></div>
            <div><dt>Last backup</dt><dd>{backup.lastBackupAt ? formatBackupDate(backup.lastBackupAt) : "Not backed up yet"}</dd></div>
          </dl>
          <div className="button-grid">
            <button className="button button--primary" type="button" disabled={!snapshot.isOnline || backup.isBusy} onClick={() => execute(() => controller.backupNow(), { successMessage: "Backup completed." })}>{backup.isBusy ? "Working…" : "Back up now"}</button>
            <button className="button button--secondary" type="button" disabled={!snapshot.isOnline || backup.isBusy || restoring} onClick={restore}>{restoring ? "Restoring…" : "Restore latest"}</button>
          </div>
          <button className="text-button" type="button" disabled={backup.isBusy} onClick={() => execute(() => controller.disconnectGoogleDrive(), { successMessage: "Google Drive disconnected." })}>Disconnect Google Drive</button>
        </>
      ) : (
        <div className="empty-state empty-state--compact">
          <strong>No Drive account linked</strong>
          <p>Connecting grants access only to this app’s hidden backup area. Your data never passes through the app server.</p>
          <button className="button button--primary" type="button" disabled={!snapshot.isOnline || backup.isBusy} onClick={() => execute(() => controller.connectGoogleDrive(), { successMessage: "Google Drive connected." })}>Connect Google Drive</button>
        </div>
      )}
    </article>
  );
}

function proteinFactor(kind: GoalKind) {
  if (kind === "gain") return "1.7 g/kg/day";
  if (kind === "lose") return "2.0 g/kg/day";
  return "1.8 g/kg/day";
}

function checkInLabel(snapshot: AppSnapshot) {
  if (snapshot.checkIn.status === "overdue") return "Overdue — complete to resume new logging";
  if (snapshot.checkIn.status === "due" || snapshot.checkIn.status === "draft") return "Due now";
  return `Next due ${new Date(`${snapshot.checkIn.dueDate}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
}

function formatBackupDate(value: string) {
  return new Date(value).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function normalizeGoals(goals: readonly NutrientGoal[]): NutrientGoal[] {
  return goals.map((goal) => {
    const definition = goalNutrientDefinition(goal.nutrientId);
    return {
      ...goal,
      nutrientId: definition?.id ?? goal.nutrientId.trim(),
      name: definition?.name ?? goal.name.trim(),
      unit: definition?.unit ?? normalizeNutrientUnit(goal.unit),
    };
  });
}

function goalNutrientDefinition(id: string): GoalNutrientDefinition | undefined {
  if (id === FIBRE_GOAL_DEFINITION.id) return FIBRE_GOAL_DEFINITION;
  return getNutrientDefinition(id);
}

function GoalNutrientOptionGroups({ definitions }: { definitions: readonly GoalNutrientDefinition[] }) {
  return (
    <>
      {definitions.some((definition) => definition.category === "other") && (
        <optgroup label="Other">
          {definitions.filter((definition) => definition.category === "other").map((definition) => <option key={definition.id} value={definition.id}>{definition.name}</option>)}
        </optgroup>
      )}
      <optgroup label="Vitamins">
        {definitions.filter((definition) => definition.category === "vitamin").map((definition) => <option key={definition.id} value={definition.id}>{definition.name}</option>)}
      </optgroup>
      <optgroup label="Minerals">
        {definitions.filter((definition) => definition.category === "mineral").map((definition) => <option key={definition.id} value={definition.id}>{definition.name}</option>)}
      </optgroup>
    </>
  );
}
